<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">My Dashboard</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript:void(0)">Employee Portal</a></li>
                    <li class="breadcrumb-item active">Dashboard</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading your dashboard...</p>
          </div>

          <div v-else-if="dashboardData">
            <!-- Welcome Section -->
            <div class="row">
              <div class="col-12">
                <div class="card bg-primary bg-gradient text-white">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-md-8">
                        <h4 class="text-white mb-2">Welcome back, {{ dashboardData.employee.firstName }}! 👋</h4>
                        <p class="text-white-50 mb-0">
                          <i class="mdi mdi-calendar me-1"></i> {{ getCurrentDate() }}
                          <span class="mx-2">|</span>
                          <i class="mdi mdi-office-building me-1"></i> {{ dashboardData.employee.department }}
                        </p>
                      </div>
                      <div class="col-md-4 text-md-end mt-3 mt-md-0">
                        <button @click="refreshDashboard" class="btn btn-light btn-sm me-2">
                          <i class="mdi mdi-refresh me-1"></i> Refresh
                        </button>
                        <button @click="gotTo('employee-profile')" class="btn btn-outline-light btn-sm">
                          <i class="mdi mdi-account me-1"></i> My Profile
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Today's Status -->
            <div class="row">
              <div class="col-12">
                <div class="card border-start border-4 border-info">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-md-8">
                        <h5 class="mb-2">
                          <i class="mdi mdi-clock-outline text-info me-2"></i>
                          Today's Status - {{ dashboardData.todayStatus.dayOfWeek }}
                        </h5>
                        <div class="d-flex flex-wrap gap-3">
                          <div v-if="dashboardData.todayStatus.clockedIn">
                            <span class="badge bg-success-subtle text-success font-size-14 px-3 py-2">
                              <i class="mdi mdi-clock-check me-1"></i>
                              Clocked In: {{ dashboardData.todayStatus.attendance.checkInTime }}
                            </span>
                          </div>
                          <div v-else>
                            <span class="badge bg-warning-subtle text-warning font-size-14 px-3 py-2">
                              <i class="mdi mdi-clock-alert me-1"></i>
                              Not Clocked In Yet
                            </span>
                          </div>

                          <div v-if="dashboardData.todayStatus.clockedOut">
                            <span class="badge bg-info-subtle text-info font-size-14 px-3 py-2">
                              <i class="mdi mdi-clock-end me-1"></i>
                              Clocked Out: {{ dashboardData.todayStatus.attendance.checkOutTime }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4 text-md-end mt-3 mt-md-0">
                        <button v-if="!dashboardData.todayStatus.clockedIn"
                                @click="clockIn"
                                class="btn btn-success">
                          <i class="mdi mdi-clock-in me-1"></i> Clock In
                        </button>
                        <button v-else-if="!dashboardData.todayStatus.clockedOut"
                                @click="clockOut"
                                class="btn btn-danger">
                          <i class="mdi mdi-clock-out me-1"></i> Clock Out
                        </button>
                        <button v-else class="btn btn-secondary" disabled>
                          <i class="mdi mdi-check-circle me-1"></i> Day Complete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Stats Cards -->
            <div class="row">
              <!-- Attendance Card -->
              <div class="col-xl-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h6 class="text-muted text-uppercase mb-2">Attendance Rate</h6>
                        <h3 class="mb-0">{{ dashboardData.quickStats.attendance.rate }}%</h3>
                        <p class="text-muted mb-0 mt-2">
                          <small>{{ dashboardData.quickStats.attendance.presentDays }}/{{ dashboardData.quickStats.attendance.workingDays }} days</small>
                        </p>
                      </div>
                      <div class="avatar-sm">
                        <span class="avatar-title bg-success-subtle text-success rounded-3">
                          <i class="mdi mdi-calendar-check font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="mt-3">
                      <div class="progress" style="height: 5px;">
                        <div class="progress-bar bg-success"
                             :style="{ width: dashboardData.quickStats.attendance.rate + '%' }">
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Leave Balance Card -->
              <div class="col-xl-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h6 class="text-muted text-uppercase mb-2">Leave Balance</h6>
                        <h3 class="mb-0">{{ getTotalLeaveBalance() }} days</h3>
                        <p class="text-muted mb-0 mt-2">
                          <small>{{ dashboardData.quickStats.leave.pendingRequests }} pending</small>
                        </p>
                      </div>
                      <div class="avatar-sm">
                        <span class="avatar-title bg-info-subtle text-info rounded-3">
                          <i class="mdi mdi-airplane font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="mt-3">
                      <button @click="gotTo('employee-leave-apply')" class="btn btn-sm btn-info w-100">
                        Apply for Leave
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Working Hours Card -->
              <div class="col-xl-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h6 class="text-muted text-uppercase mb-2">Hours This Month</h6>
                        <h3 class="mb-0">{{ dashboardData.quickStats.attendance.totalHours }}h</h3>
                        <p class="text-muted mb-0 mt-2">
                          <small>Avg: {{ dashboardData.quickStats.attendance.avgHoursPerDay }}h/day</small>
                        </p>
                      </div>
                      <div class="avatar-sm">
                        <span class="avatar-title bg-warning-subtle text-warning rounded-3">
                          <i class="mdi mdi-clock-time-eight font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="mt-3">
                      <button @click="gotTo('employee-attendance-records')" class="btn btn-sm btn-warning w-100">
                        View Attendance
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Alerts Card -->
              <div class="col-xl-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h6 class="text-muted text-uppercase mb-2">Pending Actions</h6>
                        <h3 class="mb-0">{{ getTotalAlerts() }}</h3>
                        <p class="text-muted mb-0 mt-2">
                          <small>{{ dashboardData.quickStats.documents.expiringSoon }} docs expiring</small>
                        </p>
                      </div>
                      <div class="avatar-sm">
                        <span class="avatar-title bg-danger-subtle text-danger rounded-3">
                          <i class="mdi mdi-alert-circle font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="mt-3">
                      <button @click="gotTo('employee-documents-view')" class="btn btn-sm btn-danger w-100">
                        View Documents
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Alerts Section -->
            <div v-if="dashboardData.alerts.expiringDocuments.length > 0" class="row">
              <div class="col-12">
                <div class="alert alert-warning alert-dismissible fade show" role="alert">
                  <i class="mdi mdi-alert-circle-outline me-2"></i>
                  <strong>Action Required!</strong> You have {{ dashboardData.alerts.expiringDocuments.length }} document(s) expiring soon.
                  <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                  <hr>
                  <ul class="mb-0">
                    <li v-for="doc in dashboardData.alerts.expiringDocuments" :key="doc.id">
                      <strong>{{ doc.name }}</strong> expires on {{ formatDate(doc.validTill) }}
                      ({{ doc.daysUntilExpiry }} days remaining)
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Main Content Row -->
            <div class="row">
              <!-- Recent Attendance -->
              <div class="col-xl-6">
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-calendar-clock text-primary me-2"></i>
                      Recent Attendance
                    </h5>
                    <button @click="gotTo('employee-attendance-records')" class="btn btn-sm btn-link">
                      View All <i class="mdi mdi-arrow-right"></i>
                    </button>
                  </div>
                  <div class="card-body">
                    <div v-if="dashboardData.recentActivity.attendance.length === 0" class="text-center py-4">
                      <i class="mdi mdi-calendar-blank text-muted font-size-48"></i>
                      <p class="text-muted mt-2">No recent attendance records</p>
                    </div>
                    <div v-else class="table-responsive">
                      <table class="table table-hover mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Check In</th>
                          <th>Check Out</th>
                          <th>Hours</th>
                          <th>Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="record in dashboardData.recentActivity.attendance" :key="record.date">
                          <td>{{ formatDate(record.date) }}</td>
                          <td>
                              <span v-if="record.checkInTime" class="text-success">
                                <i class="mdi mdi-clock-in"></i> {{ record.checkInTime }}
                              </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td>
                              <span v-if="record.checkOutTime" class="text-danger">
                                <i class="mdi mdi-clock-out"></i> {{ record.checkOutTime }}
                              </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td>{{ record.hoursWorked || '0' }}h</td>
                          <td>
                            <span class="badge bg-success">{{ record.status || 'Present' }}</span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Recent Leave Requests -->
              <div class="col-xl-6">
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-airplane-takeoff text-info me-2"></i>
                      Recent Leave Requests
                    </h5>
                    <button @click="gotTo('employee-leave-history')" class="btn btn-sm btn-link">
                      View All <i class="mdi mdi-arrow-right"></i>
                    </button>
                  </div>
                  <div class="card-body">
                    <div v-if="dashboardData.recentActivity.leaves.length === 0" class="text-center py-4">
                      <i class="mdi mdi-airplane-off text-muted font-size-48"></i>
                      <p class="text-muted mt-2">No recent leave requests</p>
                    </div>
                    <div v-else class="table-responsive">
                      <table class="table table-hover mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Leave Type</th>
                          <th>Period</th>
                          <th>Days</th>
                          <th>Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="leave in dashboardData.recentActivity.leaves" :key="leave.id">
                          <td>{{ leave.leaveTypeName }}</td>
                          <td>
                            <small>
                              {{ formatDate(leave.startDate) }}<br>
                              to {{ formatDate(leave.endDate) }}
                            </small>
                          </td>
                          <td>{{ leave.totalDays }}</td>
                          <td>
                              <span :class="getLeaveStatusClass(leave.status)">
                                {{ leave.status }}
                              </span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Upcoming Holidays and Announcements Row -->
            <div class="row">
              <!-- Upcoming Holidays -->
              <div class="col-xl-6">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-calendar-star text-warning me-2"></i>
                      Upcoming Holidays
                    </h5>
                  </div>
                  <div class="card-body">
                    <div v-if="dashboardData.upcoming.holidays.length === 0" class="text-center py-4">
                      <i class="mdi mdi-calendar-blank text-muted font-size-48"></i>
                      <p class="text-muted mt-2">No upcoming holidays</p>
                    </div>
                    <div v-else>
                      <div v-for="holiday in dashboardData.upcoming.holidays"
                           :key="holiday.id"
                           class="d-flex mb-3 pb-3 border-bottom">
                        <div class="flex-shrink-0">
                          <div class="avatar-sm">
                            <span class="avatar-title bg-warning-subtle text-warning rounded">
                              <i class="mdi mdi-calendar"></i>
                            </span>
                          </div>
                        </div>
                        <div class="flex-grow-1 ms-3">
                          <h6 class="mb-1">{{ holiday.name }}</h6>
                          <p class="text-muted mb-0">
                            <i class="mdi mdi-calendar-clock me-1"></i>
                            {{ formatDate(holiday.date) }}
                          </p>
                          <small class="text-muted">{{ holiday.type }}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Announcements -->
              <div class="col-xl-6">
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-bullhorn text-primary me-2"></i>
                      Latest Announcements
                    </h5>
                    <button @click="gotTo('employee-announcements')" class="btn btn-sm btn-link">
                      View All <i class="mdi mdi-arrow-right"></i>
                    </button>
                  </div>
                  <div class="card-body">
                    <div v-if="dashboardData.announcements.length === 0" class="text-center py-4">
                      <i class="mdi mdi-bullhorn-outline text-muted font-size-48"></i>
                      <p class="text-muted mt-2">No announcements</p>
                    </div>
                    <div v-else>
                      <div v-for="announcement in dashboardData.announcements"
                           :key="announcement.id"
                           class="d-flex mb-3 pb-3 border-bottom">
                        <div class="flex-shrink-0">
                          <div class="avatar-sm">
                            <span :class="getAnnouncementClass(announcement.priority)">
                              <i class="mdi mdi-bullhorn"></i>
                            </span>
                          </div>
                        </div>
                        <div class="flex-grow-1 ms-3">
                          <h6 class="mb-1">{{ announcement.title }}</h6>
                          <p class="text-muted mb-1" style="font-size: 0.875rem;">
                            {{ truncateText(announcement.content, 100) }}
                          </p>
                          <small class="text-muted">
                            <i class="mdi mdi-clock-outline me-1"></i>
                            {{ formatDate(announcement.publish_date) }}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Leave Balance Breakdown -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-chart-donut text-info me-2"></i>
                      Leave Balance Breakdown
                    </h5>
                  </div>
                  <div class="card-body">
                    <div v-if="dashboardData.quickStats.leave.balances.length === 0" class="text-center py-4">
                      <i class="mdi mdi-information-outline text-muted font-size-48"></i>
                      <p class="text-muted mt-2">No leave balance information available</p>
                    </div>
                    <div v-else class="row">
                      <div v-for="balance in dashboardData.quickStats.leave.balances"
                           :key="balance.leaveTypeName"
                           class="col-md-3 col-sm-6 mb-3">
                        <div class="card border h-100">
                          <div class="card-body text-center">
                            <h6 class="text-muted mb-3">{{ balance.leaveTypeName }}</h6>
                            <div class="mb-3">
                              <h3 class="text-primary mb-0">{{ balance.remainingDays }}</h3>
                              <small class="text-muted">of {{ balance.totalDays }} days</small>
                            </div>
                            <div class="progress" style="height: 8px;">
                              <div class="progress-bar bg-primary"
                                   :style="{ width: getLeavePercentage(balance) + '%' }">
                              </div>
                            </div>
                            <small class="text-muted mt-2 d-block">
                              Used: {{ balance.usedDays }} days
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Latest Payslip -->
            <div v-if="dashboardData.latestPayslip" class="row">
              <div class="col-12">
                <div class="card bg-light">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-md-8">
                        <h5 class="mb-2">
                          <i class="mdi mdi-cash-multiple text-success me-2"></i>
                          Latest Payslip
                        </h5>
                        <p class="text-muted mb-0">
                          Pay Period: {{ formatDate(dashboardData.latestPayslip.payPeriodStart) }} -
                          {{ formatDate(dashboardData.latestPayslip.payPeriodEnd) }}
                        </p>
                        <h4 class="text-success mt-2 mb-0">
                          KES {{ formatCurrency(dashboardData.latestPayslip.netPay) }}
                        </h4>
                      </div>
                      <div class="col-md-4 text-md-end mt-3 mt-md-0">
                        <button @click="gotTo('employee-payroll-payslips')" class="btn btn-success">
                          <i class="mdi mdi-download me-1"></i> View Payslip
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeDashboard",
  components: { NavigationBar },

  data() {
    return {
      loading: false,
      dashboardData: null
    };
  },

  mounted() {
    this.loadDashboard();
  },

  methods: {
    async loadDashboard() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeDashboard");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.dashboardData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load dashboard');
        }
      } catch (error) {
        this.$toast?.error("Failed to load dashboard: " + error.message) ||
        console.error("Failed to load dashboard:", error);
      } finally {
        this.loading = false;
      }
    },

    refreshDashboard() {
      this.loadDashboard();
    },

    clockIn() {
      // TODO: Implement clock in functionality
      this.$toast?.info("Clock in functionality coming soon") ||
      alert("Clock in functionality coming soon");
    },

    clockOut() {
      // TODO: Implement clock out functionality
      this.$toast?.info("Clock out functionality coming soon") ||
      alert("Clock out functionality coming soon");
    },

    getCurrentDate() {
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      return new Date().toLocaleDateString('en-US', options);
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    formatCurrency(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    getTotalLeaveBalance() {
      if (!this.dashboardData || !this.dashboardData.quickStats.leave.balances) {
        return 0;
      }
      return this.dashboardData.quickStats.leave.balances.reduce((total, balance) => {
        return total + (parseFloat(balance.remainingDays) || 0);
      }, 0);
    },

    getTotalAlerts() {
      if (!this.dashboardData) return 0;
      return (
          this.dashboardData.quickStats.leave.pendingRequests +
          this.dashboardData.quickStats.imprest.pendingRequests +
          this.dashboardData.quickStats.documents.expiringSoon
      );
    },

    getLeavePercentage(balance) {
      if (!balance.totalDays || balance.totalDays == 0) return 0;
      return Math.round((balance.remainingDays / balance.totalDays) * 100);
    },

    getLeaveStatusClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'approved') return 'badge bg-success';
      if (statusLower === 'rejected') return 'badge bg-danger';
      if (statusLower === 'pending') return 'badge bg-warning';
      return 'badge bg-secondary';
    },

    getAnnouncementClass(priority) {
      const priorityLower = priority?.toLowerCase();
      if (priorityLower === 'high' || priorityLower === 'urgent') {
        return 'avatar-title bg-danger-subtle text-danger rounded';
      }
      if (priorityLower === 'medium') {
        return 'avatar-title bg-warning-subtle text-warning rounded';
      }
      return 'avatar-title bg-info-subtle text-info rounded';
    },

    truncateText(text, maxLength) {
      if (!text) return '';
      // Strip HTML tags
      const stripped = text.replace(/<[^>]*>/g, '');
      if (stripped.length <= maxLength) return stripped;
      return stripped.substring(0, maxLength) + '...';
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
.card-stats {
  transition: all 0.3s ease;
}

.card-stats:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.avatar-sm {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
}

.table-hover tbody tr:hover {
  background-color: rgba(91, 115, 232, 0.05);
  cursor: pointer;
}

.progress {
  background-color: rgba(0, 0, 0, 0.1);
}

.border-4 {
  border-width: 4px !important;
}

/* Badge variants with subtle backgrounds */
.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-info-subtle {
  background-color: rgba(23, 162, 184, 0.1);
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-danger-subtle {
  background-color: rgba(220, 53, 69, 0.1);
}

.text-success {
  color: #28a745 !important;
}

.text-info {
  color: #17a2b8 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-danger {
  color: #dc3545 !important;
}

@media (max-width: 768px) {
  .card-stats {
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.5rem;
  }
}

/* Animation for cards */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card {
  animation: fadeInUp 0.5s ease;
}
</style>